package com.example.mystybag.common.ui.theme

import coil3.ImageLoader
import coil3.PlatformContext
import coil3.memory.MemoryCache
import coil3.network.ktor3.KtorNetworkFetcherFactory
import coil3.request.crossfade
import coil3.svg.SvgDecoder
import coil3.util.DebugLogger
import com.example.mystybag.core.expected.CoreDispatchers
import com.example.mystybag.commonData.expected.isInRelease
import kotlinx.coroutines.Dispatchers

/**
 * JS actual: explicit network fetcher + SVG decoder, no disk cache.
 *
 * Why explicit `KtorNetworkFetcherFactory` here (and not on Android / iOS):
 * `coil-network-ktor3` registers its NetworkFetcher via `ServiceLoader`
 * (META-INF/services). On Kotlin/JS there is no `ServiceLoader`, so the
 * auto-registration never runs and Coil silently has no fetcher capable of
 * loading `http(s)://` URLs — every network image fails with no log.
 * See Coil issue #2832 for the same root cause / fix.
 *
 * `KtorNetworkFetcherFactory()` with no args defers to `HttpClient()`, which
 * on Kotlin/JS auto-selects the browser `fetch()` engine. No disk cache
 * because Okio's `JsPlatform.tmpdir` is not a function in the browser.
 */
actual fun newImageLoader(context: PlatformContext): ImageLoader {
    return ImageLoader.Builder(context)
        .components {
            add(KtorNetworkFetcherFactory())
            add(SvgDecoder.Factory())
        }
        .memoryCache {
            MemoryCache.Builder()
                .maxSizePercent(context, percent = 0.05)
                .build()
        }
        .diskCache(null)
        .fetcherCoroutineContext(CoreDispatchers.default)
        .decoderCoroutineContext(Dispatchers.Default)
        .logger(if (isInRelease()) null else DebugLogger())
        .crossfade(false)
        .build()
}
